import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { IBudget } from "../../../../models/budgetV2";
import { fetchBudgetV2 } from "../../../../api/fetchBudgetV2";
import { ITruckerPayPlanData } from "../../../../models/truckerPayPlan";
import { fetchContractDetails } from "../../../../api/fetchContractDetails";
import { Asset } from "../../../../models/asset";
import { useBudgetAttachments } from "../../hooks/useBudgetAttachments";

export interface IUseBudgetDetailsOverrides {
    budgetId?: string;
    version?: number;
}

export const useBudgetDetails = (persona: string, overrides?: IUseBudgetDetailsOverrides) => {
    const params = useParams<{ budgetId: string; version: string }>();
    const budgetId = overrides?.budgetId ?? params.budgetId;
    const version = overrides?.version !== undefined ? String(overrides.version) : params.version;

    const budgetVersion = useMemo(() => {
        const parsed = Number(version);
        return Number.isFinite(parsed) ? parsed : undefined;
    }, [version]);

    const [budget, setBudget] = useState<IBudget | null>(null);
    const [truckerPayPlan, setTruckerPayPlan] = useState<ITruckerPayPlanData | null>(null);
    const [loading, setLoading] = useState(true);
    const { attachments } = useBudgetAttachments(budgetId);

    useEffect(() => {
        if (!budgetId || budgetVersion === undefined) {
            setLoading(false);
            return;
        }

        const fetchBudget = async () => {
            setLoading(true);
            try {
                const budgetResponse = await fetchBudgetV2(budgetId, budgetVersion);
                setBudget(budgetResponse);
            } finally {
                setLoading(false);
            }
        };

        fetchBudget();
    }, [budgetId, budgetVersion]);

    useEffect(() => {
        if (!budget?.vehicleChassis) return;

        const fetchTruckerPayPlan = async () => {
            const truckerPayPlanResponse = await fetchContractDetails({
                chassis: budget.vehicleChassis || '',
                odometer: budget.vehicleOdometer || 0,
                hourmeter: budget.vehicleHourMeter || 0,
            } as Asset);
            if (truckerPayPlanResponse) setTruckerPayPlan(truckerPayPlanResponse);
        };
        fetchTruckerPayPlan();
    }, [budget?.vehicleChassis, budget?.vehicleOdometer, budget?.vehicleHourMeter]);

    return {
        budget,
        truckerPayPlan,
        loading,
        attachments,
    };
};
